/**
 * TopBar — top navigation bar with notification popover and settings for client users
 */
import { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import useAuth from '../../auth/useAuth';
import useNotifications from '../../hooks/useNotifications';
import { useTransitionNavigate } from '../../hooks/useNavigationDirection';
import { markAsRead, markAllAsRead } from '../../services/notificationService';
import { NOTIFICATION_TYPES } from '../../services/notificationTypes';
import Icon from '../common/Icon';
import FeedbackModal from '../common/FeedbackModal';

const typeIcons = {
  [NOTIFICATION_TYPES.CHECKIN_REVIEWED]: 'ClipboardCheck',
  [NOTIFICATION_TYPES.CHECKIN_SUBMITTED]: 'ClipboardCheck',
  [NOTIFICATION_TYPES.PROGRAM_ASSIGNED]: 'BookOpen',
  [NOTIFICATION_TYPES.MESSAGE_RECEIVED]: 'MessageCircle',
  [NOTIFICATION_TYPES.SKILL_FOCUSED]: 'Target',
  [NOTIFICATION_TYPES.SKILL_SKIPPED]: 'SkipForward',
  [NOTIFICATION_TYPES.PRACTICE_LOGGED]: 'Dumbbell',
  [NOTIFICATION_TYPES.MILESTONE_COMPLETED]: 'Trophy',
  [NOTIFICATION_TYPES.MODULE_ASSIGNED]: 'BookMarked',
  [NOTIFICATION_TYPES.MODULE_COMPLETED]: 'GraduationCap',
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TopBar = () => {
  const { user, isTrainer, isBeta } = useAuth();
  const { data: notifications, unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [showBetaTip, setShowBetaTip] = useState(
    () => isBeta && !localStorage.getItem('betaTipDismissed')
  );
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const settingsPath = isTrainer ? '/trainer/settings' : '/settings';
  const onSettings = location.pathname === settingsPath;

  const closePopover = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 200);
  };

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id).catch(() => {});
    }
    closePopover();
    navigate(notification.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead(user.uid);
    } catch {
      // silent
    }
  };

  const validNotifications = (notifications || []).filter((n) => n.title || n.message);

  return (
    <header className="top-bar">
      <div className="top-bar__inner">
      <div className="top-bar__actions top-bar__actions--left">
        <NavLink to={isTrainer ? '/trainer/dashboard' : '/'} data-nav-direction="lateral" className="top-bar__title">
          <img src="/logo.svg" alt="Thrive Companions" className="top-bar__logo" />
        </NavLink>
      </div>
      <div className="top-bar__actions">
        {isBeta && (
          <div className="top-bar__tip-anchor">
            <button
              className="top-bar__action"
              onClick={() => setFeedbackOpen(true)}
              aria-label="Report an issue"
            >
              <Icon name="Bug" size={20} aria-hidden="true" />
            </button>
            {showBetaTip && (
              <div className="beta-tip" role="status">
                <span className="beta-tip__badge"><Icon name="Sparkles" size={12} /> New!</span>
                <span className="beta-tip__text">Tap here to report bugs or share feedback</span>
                <button
                  className="beta-tip__close"
                  onClick={(e) => {
                    e.stopPropagation();
                    localStorage.setItem('betaTipDismissed', '1');
                    setShowBetaTip(false);
                  }}
                  aria-label="Dismiss tip"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            )}
          </div>
        )}
        <a
          href="https://thrivecompanions.com"
          target="_blank"
          rel="noopener noreferrer"
          className="top-bar__action"
          aria-label="Visit website"
        >
          <Icon name="Globe" size={20} aria-hidden="true" />
        </a>
        <div className="top-bar__popover-container">
          <button
            ref={buttonRef}
            className={`top-bar__action${open ? ' top-bar__action--active' : ''}`}
            onClick={() => open ? closePopover() : setOpen(true)}
            aria-label="Alerts"
            aria-expanded={open}
          >
            <Icon name="Bell" size={20} aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="top-bar__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>

          {open && (
            <div className={`notification-popover${closing ? ' notification-popover--closing' : ''}`} ref={popoverRef}>
              <div className="notification-popover__header">
                <h3 className="notification-popover__title">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    className="button button--text button--small"
                    onClick={handleMarkAllRead}
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {validNotifications.length > 0 ? (
                <div className="notification-popover__list">
                  {validNotifications.map((n) => (
                    <div
                      key={n.id}
                      className={`notification-popover__item${!n.read ? ' notification-popover__item--unread' : ''}`}
                      onClick={() => handleNotificationClick(n)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleNotificationClick(n); }}
                    >
                      <span className="notification-popover__icon" aria-hidden="true">
                        <Icon name={typeIcons[n.type] || 'Bell'} size={18} />
                      </span>
                      <div className="notification-popover__content">
                        <p className="notification-popover__item-title">{n.title}</p>
                        <p className="notification-popover__item-message">{n.message}</p>
                      </div>
                      <span className="notification-popover__time">{formatTime(n.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="notification-popover__empty">No notifications</p>
              )}
            </div>
          )}
        </div>

        <button
          className={`top-bar__action${onSettings ? ' top-bar__action--active' : ''}`}
          onClick={() => onSettings ? navigate(-1) : navigate(settingsPath)}
          aria-label="Settings"
        >
          <Icon name="Settings" size={20} aria-hidden="true" />
        </button>
      </div>
      </div>
      {isBeta && <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />}
    </header>
  );
};

export default TopBar;
